/**
 * Authentication Service
 *
 * Firebase Auth implementation:
 * - Magic link (passwordless email authentication)
 * - Social providers (Google, GitHub)
 * - Session management
 * - Profile management
 *
 * @file Authentication service using Firebase Auth
 */

import {apiClient} from '@/shared/lib/api';
import {resetUser, trackSignOut, trackSignUp} from '@/shared/utils/mixpanel';
import {logger} from '@/shared/lib/logger/logger';
import {firebaseAuthService} from './firebase.service';
import {getFunctionsUrl, isDevelopment} from '@/shared/config/environment';
import type {
    AuthServiceResponse,
    AuthSession,
    FirebaseUser,
    SignInWithMagicLinkParams,
    SignInWithProviderParams,
} from '../types';

/**
 * Authentication Service
 *
 * Provides a clean interface for all authentication operations
 * using Firebase Auth
 */
export class AuthService {
  // ============================================================================
  // MAGIC LINK AUTHENTICATION
  // ============================================================================

  /**
   * Send magic link for passwordless authentication
   *
   * Uses Firebase Auth sendSignInLinkToEmail
   *
   * @param params - Magic link parameters
   * @returns Promise with success/error
   */
  async sendMagicLink(params: SignInWithMagicLinkParams): Promise<AuthServiceResponse> {
    const { email, redirectTo } = params;
    const result = await firebaseAuthService.sendMagicLink(email, redirectTo);

    if (result.error) {
      return { error: result.error };
    }

    // Save email for convenience
    this.saveEmailToLocalStorage(email);

    // Track magic link request
    trackSignUp('magic_link');

    return { data: undefined };
  }

  // ============================================================================
  // SOCIAL AUTHENTICATION (OAuth Providers)
  // ============================================================================

  /**
   * Sign in with OAuth provider (Google, GitHub)
   *
   * @param params - Provider sign-in parameters
   * @returns Promise with success/error
   */
  async signInWithProvider(params: SignInWithProviderParams): Promise<AuthServiceResponse> {
    const { provider } = params;

    try {
      if (provider === 'google') {
        const result = await firebaseAuthService.signInWithGoogle(false); // Use popup for better UX
        if (result.error) {
          return { error: result.error };
        }
        // If user closed popup, result.data will be undefined - this is OK
        if (result.data) {
          // Track OAuth sign-in attempt
          trackSignUp('google');
        }
        return { data: undefined };
      } else {
        // TypeScript knows provider is 'github' here, but keep check for runtime safety
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Runtime safety check
        if (provider === 'github') {
        const result = await firebaseAuthService.signInWithGithub(false); // Use popup for better UX
        if (result.error) {
          return { error: result.error };
        }
        // If user closed popup, result.data will be undefined - this is OK
        if (result.data) {
          // Track OAuth sign-in attempt
          trackSignUp('github');
        }
        return { data: undefined };
      } else {
        // This should never happen due to TypeScript type checking, but handle defensively
        const providerStr = provider as string;
        return { error: new Error(`Provider ${providerStr} not supported with Firebase Auth`) };
        }
      }
    } catch (error) {
      const providerStr = provider as string;
      logger.error('Firebase OAuth error', error instanceof Error ? error : new Error(String(error)), {
        component: 'AuthService',
        action: 'sign_in_with_provider',
        provider: providerStr,
      });
      return { error: error as Error };
    }
  }

  // ============================================================================
  // SESSION MANAGEMENT
  // ============================================================================

  /**
   * Get current session
   *
   * Returns Firebase session-like object
   */
  async getCurrentSession(): Promise<AuthSession | null> {
    return await firebaseAuthService.getCurrentSession();
  }

  /**
   * Get current user
   *
   * Returns Firebase User
   */
  getCurrentUser(): FirebaseUser | null {
    return firebaseAuthService.getCurrentUser();
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    return await firebaseAuthService.isAuthenticated();
  }

  /**
   * Subscribe to auth state changes
   *
   * @param callback - Callback function for auth events
   * @returns Unsubscribe function
   */
  onAuthStateChange(
    callback: (event: string, session: AuthSession | null) => void
  ): () => void {
    return firebaseAuthService.onAuthStateChange(callback);
  }

  /**
   * Sign out user
   */
  async signOut(): Promise<AuthServiceResponse> {
    const result = await firebaseAuthService.signOut();

    if (result.error) {
      return { error: result.error };
    }

    // Clear local storage
    this.clearLocalStorage();

    // Track sign-out and reset Mixpanel user
    trackSignOut();
    resetUser();

    return { data: undefined };
  }

  // ============================================================================
  // PROFILE MANAGEMENT
  // ============================================================================

  /**
   * Get user profile from profiles table
   *
   * ✅ SECURITY: Uses backend API endpoint instead of direct database access
   */
  async getUserProfile(userId?: string): Promise<{ data: unknown; error: null } | { data: null; error: Error }> {
    try {
      const currentUser = this.getCurrentUser();
      const user = userId || (currentUser?.uid || null);

      if (!user) {
        return { data: null, error: new Error('No user found') };
      }

      // ✅ USE BACKEND API: Call get-user-profile endpoint
      const session = await this.getCurrentSession();

      if (!session) {
        return { data: null, error: new Error('Not authenticated') };
      }

      const url = new URL(`${getFunctionsUrl()}/users/profile`);

      // Use session access token for authentication
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`
      };

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        const errorDataRaw = await response.json().catch(() => ({ error: 'Unknown error' })) as unknown;
        const errorData = typeof errorDataRaw === 'object' && errorDataRaw !== null
          ? errorDataRaw as { error?: string }
          : { error: 'Unknown error' };
        const errorMessage = typeof errorData.error === 'string'
          ? errorData.error
          : `Failed to fetch profile: ${response.status}`;
        logger.error('Get profile error', new Error(errorMessage), {
          component: 'AuthService',
          action: 'get_user_profile',
          userId: user,
          status: response.status,
        });
        return { data: null, error: new Error(errorMessage) };
      }

      const data = await response.json() as { success: boolean; profile?: unknown; error?: string };

      if (!data.success || !data.profile) {
        return { data: null, error: new Error(data.error || 'Failed to fetch profile') };
      }

      return { data: data.profile, error: null };
    } catch (error) {
      logger.error('Get profile exception', error instanceof Error ? error : new Error(String(error)), {
        component: 'AuthService',
        action: 'get_user_profile',
      });
      return { data: null, error: error as Error };
    }
  }

  /**
   * Update user profile in profiles table
   *
   * ✅ Uses dedicated update-user-profile API endpoint for secure profile updates.
   * Users can only update their own profile (enforced by backend authentication).
   */
  async updateUserProfile(updates: { first_name?: string; last_name?: string }): Promise<{ data?: undefined; error?: Error }> {
    try {
      const user = this.getCurrentUser();

      if (!user) {
        return { error: new Error('No user found') };
      }

      // ✅ Use dedicated API endpoint for secure profile updates
      const response = await apiClient.updateUserProfile(updates);

      if (!response.success) {
        const errorMessage = 'Failed to update profile';
        logger.error('Update profile error', new Error(errorMessage), {
          component: 'AuthService',
          action: 'update_user_profile',
        });
        return { error: new Error(errorMessage) };
      }

      return { data: undefined };
    } catch (error) {
      logger.error('Update profile exception', error instanceof Error ? error : new Error(String(error)), {
        component: 'AuthService',
        action: 'update_user_profile',
      });
      return { error: error as Error };
    }
  }

  /**
   * Check if user is admin
   *
   * ✅ SECURITY: Uses backend API endpoint instead of direct database access
   *
   * 🛠️ DEV MODE: In development, always returns true to bypass admin checks
   */
  async isAdmin(userId?: string): Promise<boolean> {
    try {
      // 🛠️ DEV MODE: Bypass admin check in development
      if (isDevelopment()) {
        logger.debug('[DEV MODE] Admin check bypassed - allowing access', {
          component: 'AuthService',
          action: 'is_admin',
        });
        return true;
      }

      const currentUser = this.getCurrentUser();
      const user = userId || (currentUser?.uid || null);

      if (!user) {
        return false;
      }

      // Get session for authentication
      const session = await this.getCurrentSession();

      if (!session?.access_token) {
        logger.error('Failed to check admin status: Not authenticated', new Error('Not authenticated'), {
          component: 'AuthService',
          action: 'is_admin',
        });
        return false;
      }

      // ✅ USE BACKEND API: Call check-admin-status endpoint with session token
      const url = new URL(`${getFunctionsUrl()}/users/check-admin-status`);

      // Use session access token for authentication
      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify({})
      });

      if (!response.ok) {
        const errorDataRaw = await response.json().catch(() => ({ error: 'Unknown error' })) as unknown;
        const errorData = typeof errorDataRaw === 'object' && errorDataRaw !== null
          ? errorDataRaw as { error?: string }
          : { error: 'Unknown error' };
        logger.error('Failed to check admin status', new Error(errorData.error || `HTTP ${response.status}`), {
          component: 'AuthService',
          action: 'is_admin',
          status: response.status,
        });
        return false;
      }

      const data = await response.json() as { is_admin: boolean; error?: string };

      if (data.error) {
        logger.error('Failed to check admin status', new Error(data.error), {
          component: 'AuthService',
          action: 'is_admin',
        });
        return false;
      }

      return data.is_admin || false;
    } catch (error) {
      logger.error('isAdmin check failed', error instanceof Error ? error : new Error(String(error)), {
        component: 'AuthService',
        action: 'is_admin',
      });
      return false;
    }
  }

  // ============================================================================
  // UTILITIES
  // ============================================================================

  /**
   * Save email to localStorage for convenience
   */
  private saveEmailToLocalStorage(email: string): void {
    try {
      localStorage.setItem('saved_email', email);
    } catch (error) {
      logger.error('Failed to save email', error instanceof Error ? error : new Error(String(error)), {
        component: 'AuthService',
        action: 'save_email',
      });
    }
  }

  /**
   * Get saved email from localStorage
   */
  getSavedEmail(): string | null {
    try {
      return localStorage.getItem('saved_email');
    } catch (error) {
      logger.error('Failed to get saved email', error instanceof Error ? error : new Error(String(error)), {
        component: 'AuthService',
        action: 'get_saved_email',
      });
      return null;
    }
  }

  /**
   * Remove saved email from localStorage
   */
  removeSavedEmail(): void {
    try {
      localStorage.removeItem('saved_email');
    } catch (error) {
      logger.error('Failed to remove saved email', error instanceof Error ? error : new Error(String(error)), {
        component: 'AuthService',
        action: 'remove_saved_email',
      });
    }
  }

  /**
   * Clear all auth-related localStorage
   */
  private clearLocalStorage(): void {
    this.removeSavedEmail();
  }
}

// Export singleton instance
export const authService = new AuthService();

// Re-export types for convenience
;
